<template>
  <ADSCardSection
    :isAccordion="true"
    :isLoading="false"
    sectionId="testID"
  >
    <template #header>
      Donor Acceptability Criteria EMR-OATS Preview
    </template>
    <template #body>
      <div class="sections">
        <div class="row">
          <div class="col-3 fieldName">
            In EMR
          </div>
          <div class="col-2 fieldName">
            In OATS
          </div>
        </div>
        <div class="row" v-if="organ === 'Kidney' || organ === 'Liver' || organ === 'Lung'">
          <div class="col-3">
            <ADSTextInput
              v-model="preferredDonorType"
              disabled
              label="Preferred Donor Type"
              class="preferredDonorType"
              :class="{ highlightField: isDifferent(preferredDonorType, preferredDonorTypeOATS) }"
            />
          </div>
          <div class="col-2">
            <ADSTextInput
              v-model="preferredDonorTypeOATS"
              disabled
              label="Preferred Donor Type"
              class="preferredDonorType" 
            />
          </div>
        </div>
        <div class="row" v-if="organ === 'Kidney' || organ === 'Pancreas'">
          <div class="col-3">
            <ADSTextInput
              v-model="acceptHepBCore"
              disabled
              label="Accept Hep B Core Ab+ Donor"
              :class="{ highlightField: isDifferentAcceptance( acceptHepBCore, acceptHepBCoreOATS ) }"
            />
          </div>
          <div class="col-2">
            <ADSTextInput
              v-model="acceptHepBCoreOATS"
              disabled
              label="Accept Hep B Core Ab+ Donor"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-3">
            <ADSTextInput
              v-model="acceptA2A2BDonor"
              disabled
              label="Accept A2 or A2B Donor"
              :class="{ highlightField: isDifferentAcceptance( acceptA2A2BDonor, acceptA2A2BDonorOATS ) }"
            />
          </div>
          <div class="col-2">
            <ADSTextInput
              v-model="acceptA2A2BDonorOATS"
              disabled
              label="Accept A2 or A2B Donor"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-3">
            <ADSTextInput
              v-model="acceptABOIncompatibleDonor"
              disabled
              label="Accept ABO Incompatible Donor"
              :class="{ highlightField: isDifferentAcceptance( acceptABOIncompatibleDonor, acceptABOIncompatibleDonorOATS ) }"
            />
          </div>

          <div class="col-2">
            <ADSTextInput
              v-model="acceptABOIncompatibleDonorOATS"
              disabled
              label="Accept ABO Incompatible Donor"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-3">
            <ADSTextInput
              v-model="acceptHCVAbPlusDonor"
              disabled
              label="Accept HCV Ab+ Donor"
              :class="{ highlightField: isDifferentAcceptance( acceptHCVAbPlusDonor, acceptHCVAbPlusDonorOATS ) }"
            />
          </div>
          <div class="col-2">
            <ADSTextInput
              v-model="acceptHCVAbPlusDonorOATS"
              disabled
              label="Accept HCV Ab+ Donor"
            />
          </div>

        </div>

        <div class="row">
          <div class="col-3">
            <ADSTextInput
              v-model="acceptHCVNATPlusDonor"
              disabled
              label="Accept HCV NAT+ Donor"
              :class="{ highlightField: isDifferentAcceptance( acceptHCVNATPlusDonor, acceptHCVNATPlusDonorOATS ) }"
            />
          </div>
          <div class="col-2">
            <ADSTextInput
              v-model="acceptHCVNATPlusDonorOATS"
              disabled
              label="Accept HCV NAT+ Donor"
            />
          </div>
        </div>

        <div class="row" v-if="organ === 'Kidney'">
          <div class="col-3">
            <ADSTextInput
              id="disabled-text"
              v-model="acceptExtendedCriteriaDonor"
              disabled
              label="Accept Extended Criteria Donor"
              :class="{ highlightField: isDifferentAcceptance( acceptExtendedCriteriaDonor, acceptExtendedCriteriaDonorOATS ) }"
            />
          </div>

          <div class="col-2">
            <ADSTextInput
              id="disabled-text"
              v-model="acceptExtendedCriteriaDonorOATS"
              disabled
              label="Accept Extended Criteria Donor"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-3">
            <ADSTextInput
              id="disabled-text"
              v-model="comments"
              disabled
              label="Comments"
              class="comments"
              :class="{ highlightField: isDifferent( comments, commentsOATS ) }"
            />
          </div>

          <div class="col-3">
            <ADSTextInput
              id="disabled-text"
              v-model="commentsOATS"
              disabled
              label="Comments"
              class="comments"
            />
          </div>
        </div>
      </div>
    </template>
  </ADSCardSection>
</template>

<script>
import _ from 'lodash';
import { ORGAN_LIST } from '@/constants';

export default {
  name: "DonorAcceptabilitySectionDetailsEMROATS",
  props: {
    patient: {
      type: Object,
      default: null,
    },
    patientBundle: {
      type: Object,
      default: null,
    },
    patientBundleOATS: {
      type: Object,
      default: null,
    },
    organ: {
      type: String,
      default: null,
    }
  },
  data() {
    return {
      acceptHepBCore: this.getAcceptHepBCore( this.patientBundle ),
      acceptHepBCoreOATS: this.getAcceptHepBCoreOATS( this.patientBundleOATS, this.organ, this.patientBundle ),
      preferredDonorType: this.getPreferredDonorType( this.patientBundle ),
      preferredDonorTypeOATS: this.getPreferredDonorTypeOATS( this.patientBundleOATS, this.organ, this.patientBundle ),
      acceptA2A2BDonor: this.getAcceptA2A2BDonor( this.patientBundle ),
      acceptA2A2BDonorOATS: this.getAcceptA2A2BDonorOATS( this.patientBundleOATS, this.organ, this.patientBundle ),
      acceptABOIncompatibleDonor: this.getAcceptABOIncompatibleDonor( this.patientBundle ),
      acceptABOIncompatibleDonorOATS: this.getAcceptABOIncompatibleDonorOATS( this.patientBundleOATS, this.organ, this.patientBundle ),
      acceptHCVAbPlusDonor: this.getAcceptHCVAbPlusDonor( this.patientBundle ),
      acceptHCVAbPlusDonorOATS: this.getAcceptHCVAbPlusDonorOATS( this.patientBundleOATS, this.organ, this.patientBundle ),
      acceptHCVNATPlusDonor: this.getAcceptHCVNATPlusDonor( this.patientBundle ),
      acceptHCVNATPlusDonorOATS: this.getAcceptHCVNATPlusDonorOATS( this.patientBundleOATS, this.organ, this.patientBundle ),
      acceptExtendedCriteriaDonor: this.getAcceptExtendedCriteriaDonor( this.patientBundle ),
      acceptExtendedCriteriaDonorOATS: this.getAcceptExtendedCriteriaDonorOATS( this.patientBundleOATS, this.organ, this.patientBundle ),
      comments: this.getDonorAcceptanceComments( this.patientBundle ),
      commentsOATS: this.getDonorAcceptanceCommentsOATS( this.patientBundleOATS, this.organ, this.patientBundle )
    };
    },
   mounted() {
    
    },
  methods: {
    combineProps() {
      return `${this.patient}`;
    },
    isDifferent( a, b ) {
      return String( a ?? '' ).trim().toLowerCase() !== String( b ?? '' ).trim().toLowerCase();
  },
  isDifferentAcceptance( a, b ) {
      if( a === 'Yes' && b === 'Decline' ) {
        return true;
      }
      if( a === 'Decline' && b === 'Yes' ) {
        return true;
      }
      if( a === 'No' && b === 'Accept' ) {
        return true;
      }
      if( a === 'Accept' && b === 'No' ) {
        return true;
      }
      if( a !== null && ( b === null || b === '' ) ) {
        return true;
      }
      if( ( a === null || a === '' ) && b !== null ) {
        return true;
      }
      return false;
  },
  getEpisodePhase( patientBundle ) {
      if ( !patientBundle?.entries ) {
        return;
      }
      let episodeOfCares = patientBundle.entries.filter( ( n ) => (
        _.get( n[0], 'resource.resourceType' ) === 'EpisodeOfCare' 
      ) );
      if ( !( episodeOfCares?.[0]?.[0] ) ) {
        return;
      }
      const episodeOfCare = episodeOfCares[0].filter( ( n ) => ( _.get( n, 'resource.id' ) === patientBundle.episode_id ) );
      if ( !( episodeOfCare ) ) {
        return;
      }
      const extensions = episodeOfCare?.[ 0 ]?.resource?.extension;
      if ( !extensions ) {
        return;
      }
      for ( const item of extensions ) {
        if ( item.url?.includes( 'transplant-phase-status' ) && item.extension?.length ) {
          const phaseExt = item.extension.filter( ( ext ) => ext.url === 'phase' );
          if ( phaseExt?.[0] ) {
            return _.get( phaseExt[0], 'valueCodeableConcept.coding[0].code' );
          }
        }
      }
    },
    getSmartdataComponentValue( patientBundle, observationCode, valuePath = 'valueString' ) {
      const entries = patientBundle?.entries;
      if( !entries?.length ) {
        return null;
      }
      const smartdataGroups = entries.filter( ( n ) =>
        _.get( n[0], 'resource.resourceType' ) === 'Observation' &&
        _.get( n[0], 'resource.category[0].coding[0].code' ) === 'smartdata'
      );
      const group = smartdataGroups[0];
      if( !group ) {
        return null;
      }
      const match = group.filter( ( n ) => ( _.get( n, 'resource.code.coding[0].code' ) === observationCode && 
      _.get( n, 'resource.focus[0].reference' )?.includes( patientBundle.episode_id ) ) );
      if( !match[0] ) {
        return null;
      }
      const raw = _.get( match[0], `resource.component[0].${valuePath}` );
      if( raw === null || raw === undefined ) {
        return null;
      }
      return String( raw );
    },
    getAcceptHepBCore( patientBundle ) {
      const value = this.getSmartdataComponentValue( patientBundle, 'UHN#4004', 'valueBoolean' );
      if ( value === null ) {
        return '';
      }
      return value === 'true' ? 'Yes' : 'No';
    },
    getAcceptHepBCoreOATS( patientBundleOATS, organ, patientBundle ) {
      const organCode = ORGAN_LIST?.[ organ ] ?? organ;
      const organCodeNum = organCode === null || organCode === undefined ? undefined : Number( organCode );
      let episodePhase = this.getEpisodePhase( patientBundle );
      let journey = patientBundleOATS.journeys?.filter( ( n ) => ( n.organ_code === organCodeNum && n.state !== 'cancelled' ) );
      if( journey && journey[ 0 ] && journey.length === 1 ) {
        if ( journey[ 0 ].donor_acceptability?.hep_ab_core_positive === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.hep_ab_core_positive ? 'Accept' : 'Decline';
      }
      if( journey && journey[ 0 ] && journey.length > 1 ) {
        if( episodePhase > 30 ) {
          journey = journey.filter( ( n ) => ( n.stage === 'transplant' || n.stage === 'post-transplant' ) );
          if( journey && journey[ 0 ] ) {
            if ( journey[ 0 ].donor_acceptability?.hep_ab_core_positive === null ) {
              return '';
            }
            return journey[ 0 ].donor_acceptability?.hep_ab_core_positive ? 'Accept' : 'Decline';
          }
        } else {
          journey = journey.filter( ( n ) => ( ( n.stage === 'registration' || n.stage === 'referral' || n.stage === 'assessment' || n.stage === 'waitlist' ) &&
          ( n.completed === false ) ) );
          if( journey && journey[ 0 ] ) {
            if ( journey[ 0 ].donor_acceptability?.hep_ab_core_positive === null ) {
              return '';
            }
            return journey[ 0 ].donor_acceptability?.hep_ab_core_positive ? 'Accept' : 'Decline';
          }
        }
      }
    },
    getPreferredDonorType( patientBundle ) {
      return this.getSmartdataComponentValue( patientBundle, 'UHN#4003' );
    },
    getPreferredDonorTypeOATS( patientBundleOATS, organ, patientBundle ) {
      const organCode = ORGAN_LIST?.[ organ ] ?? organ;
      const organCodeNum = organCode === null || organCode === undefined ? undefined : Number( organCode );
      let episodePhase = this.getEpisodePhase( patientBundle );
      let journey = patientBundleOATS.journeys?.filter( ( n ) => ( n.organ_code === organCodeNum && n.state !== 'cancelled' ) );
      if( journey && journey[ 0 ] && journey.length === 1 ) {
        if ( journey[ 0 ].donor_acceptability?.living_donor === true && journey[ 0 ].donor_acceptability?.deceased_donor === true ) {
          return 'Deceased or Living Donor';
        }
        if ( journey[ 0 ].donor_acceptability?.living_donor === true && journey[ 0 ].donor_acceptability?.deceased_donor === false ) {
          return 'Living Donor Only';
        }
        if ( journey[ 0 ].donor_acceptability?.living_donor === false && journey[ 0 ].donor_acceptability?.deceased_donor === true ) {
          return 'Deceased Donor Only';
        }
        return '';
      }
      if( journey && journey[ 0 ] && journey.length > 1 ) {
        if( episodePhase > 30 ) {
          journey = journey.filter( ( n ) => ( n.stage === 'transplant' || n.stage === 'post-transplant' ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.living_donor === true && journey[ 0 ].donor_acceptability?.deceased_donor === true ) {
          return 'Deceased or Living Donor';
        }
        if ( journey[ 0 ].donor_acceptability?.living_donor === true && journey[ 0 ].donor_acceptability?.deceased_donor === false ) {
          return 'Living Donor Only';
        }
        if ( journey[ 0 ].donor_acceptability?.living_donor === false && journey[ 0 ].donor_acceptability?.deceased_donor === true ) {
          return 'Deceased Donor Only';
        }
        return '';
      }
        } else {
          journey = journey.filter( ( n ) => ( ( n.stage === 'registration' || n.stage === 'referral' || n.stage === 'assessment' || n.stage === 'waitlist' ) &&
          ( n.completed === false ) ) );
           if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.living_donor === true && journey[ 0 ].donor_acceptability?.deceased_donor === true ) {
          return 'Deceased or Living Donor';
        }
        if ( journey[ 0 ].donor_acceptability?.living_donor === true && journey[ 0 ].donor_acceptability?.deceased_donor === false ) {
          return 'Living Donor Only';
        }
        if ( journey[ 0 ].donor_acceptability?.living_donor === false && journey[ 0 ].donor_acceptability?.deceased_donor === true ) {
          return 'Deceased Donor Only';
        }
        return '';
      }
        }
      }
    },
    getAcceptA2A2BDonorOATS( patientBundleOATS, organ, patientBundle ) {
      const organCode = ORGAN_LIST?.[ organ ] ?? organ;
      const organCodeNum = organCode === null || organCode === undefined ? undefined : Number( organCode );
      let episodePhase = this.getEpisodePhase( patientBundle );
      let journey = patientBundleOATS.journeys?.filter( ( n ) => ( n.organ_code === organCodeNum && n.state !== 'cancelled' ) );
      if( journey && journey[ 0 ] && journey.length === 1 ) {
        if ( journey[ 0 ].donor_acceptability?.a2_or_a2b_donor === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.a2_or_a2b_donor ? 'Accept' : 'Decline';
      }
      if( journey && journey[ 0 ] && journey.length > 1 ) {
        if( episodePhase > 30 ) {
          journey = journey.filter( ( n ) => ( n.stage === 'transplant' || n.stage === 'post-transplant' ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.a2_or_a2b_donor === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.a2_or_a2b_donor ? 'Accept' : 'Decline';
      }
        } else {
          journey = journey.filter( ( n ) => ( ( n.stage === 'registration' || n.stage === 'referral' || n.stage === 'assessment' || n.stage === 'waitlist' ) &&
          ( n.completed === false ) ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.a2_or_a2b_donor === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.a2_or_a2b_donor ? 'Accept' : 'Decline';
      }
        }
      }
    },
    getAcceptA2A2BDonor( patientBundle ) {
      const value = this.getSmartdataComponentValue( patientBundle, 'UHN#4005', 'valueBoolean' );
      if ( value === null ) {
        return '';
      }
      return value === 'true' ? 'Yes' : 'No';
    },
    getAcceptABOIncompatibleDonor( patientBundle ) {
      const value = this.getSmartdataComponentValue( patientBundle, 'UHN#4006', 'valueBoolean' );
      if ( value === null ) {
        return '';
      }
      return value === 'true' ? 'Yes' : 'No';
    },
    getAcceptABOIncompatibleDonorOATS( patientBundleOATS, organ, patientBundle ) {
      const organCode = ORGAN_LIST?.[ organ ] ?? organ;
      const organCodeNum = organCode === null || organCode === undefined ? undefined : Number( organCode );
      let journey = patientBundleOATS.journeys?.filter( ( n ) => ( n.organ_code === organCodeNum && n.state !== 'cancelled' ) );
      let episodePhase = this.getEpisodePhase( patientBundle );
      if( journey && journey[ 0 ] && journey.length === 1 ) {
        if ( journey[ 0 ].donor_acceptability?.abo_incompatible === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.abo_incompatible ? 'Accept' : 'Decline';
      }
      if( journey && journey[ 0 ] && journey.length > 1 ) {
        if( episodePhase > 30 ) {
          journey = journey.filter( ( n ) => ( n.stage === 'transplant' || n.stage === 'post-transplant' ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.abo_incompatible === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.abo_incompatible ? 'Accept' : 'Decline';
      }
        } else {
          journey = journey.filter( ( n ) => ( ( n.stage === 'registration' || n.stage === 'referral' || n.stage === 'assessment' || n.stage === 'waitlist' ) &&
          ( n.completed === false ) ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.abo_incompatible === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.abo_incompatible ? 'Accept' : 'Decline';
      }
        }
      }
    },
    getAcceptHCVAbPlusDonorOATS( patientBundleOATS, organ, patientBundle ) {
      const organCode = ORGAN_LIST?.[ organ ] ?? organ;
      const organCodeNum = organCode === null || organCode === undefined ? undefined : Number( organCode );
      let journey = patientBundleOATS.journeys?.filter( ( n ) => ( n.organ_code === organCodeNum && n.state !== 'cancelled' ) );
      let episodePhase = this.getEpisodePhase( patientBundle );
      if( journey && journey[ 0 ] && journey.length === 1 ) {
        if ( journey[ 0 ].donor_acceptability?.hcv_ab_positive === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.hcv_ab_positive ? 'Accept' : 'Decline';
      }
      if( journey && journey[ 0 ] && journey.length > 1 ) {
        if( episodePhase > 30 ) {
          journey = journey.filter( ( n ) => ( n.stage === 'transplant' || n.stage === 'post-transplant' ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.hcv_ab_positive === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.hcv_ab_positive ? 'Accept' : 'Decline';
      }
        } else {
          journey = journey.filter( ( n ) => ( ( n.stage === 'registration' || n.stage === 'referral' || n.stage === 'assessment' || n.stage === 'waitlist' ) &&
          ( n.completed === false ) ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.hcv_ab_positive === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.hcv_ab_positive ? 'Accept' : 'Decline';
      }
        }
      }
     
    },
    getAcceptHCVAbPlusDonor( patientBundle ) {
      const value = this.getSmartdataComponentValue( patientBundle, 'UHN#4007', 'valueBoolean' );
      if ( value === null ) {
        return '';
      }
      return value === 'true' ? 'Yes' : 'No';
    },
    getAcceptHCVNATPlusDonor( patientBundle ) {
      const value = this.getSmartdataComponentValue( patientBundle, 'UHN#4008', 'valueBoolean' );
      if ( value === null ) {
        return '';
      }
      return value === 'true' ? 'Yes' : 'No';
    },
    getAcceptHCVNATPlusDonorOATS( patientBundleOATS, organ, patientBundle ) {
      const organCode = ORGAN_LIST?.[ organ ] ?? organ;
      const organCodeNum = organCode === null || organCode === undefined ? undefined : Number( organCode );
      let journey = patientBundleOATS.journeys?.filter( ( n ) => ( n.organ_code === organCodeNum && n.state !== 'cancelled' ) );
      let episodePhase = this.getEpisodePhase( patientBundle );
      if( journey && journey[ 0 ] && journey.length === 1 ) {
        if ( journey[ 0 ].donor_acceptability?.hcv_nat_positive === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.hcv_nat_positive ? 'Accept' : 'Decline';
      }
      if( journey && journey[ 0 ] && journey.length > 1 ) {
        if( episodePhase > 30 ) {
          journey = journey.filter( ( n ) => ( n.stage === 'transplant' || n.stage === 'post-transplant' ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.hcv_nat_positive === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.hcv_nat_positive ? 'Accept' : 'Decline';
      }
        } else {
          journey = journey.filter( ( n ) => ( ( n.stage === 'registration' || n.stage === 'referral' || n.stage === 'assessment' || n.stage === 'waitlist' ) &&
          ( n.completed === false ) ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.hcv_nat_positive === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.hcv_nat_positive ? 'Accept' : 'Decline';
      }
        }
      }
    },
    getAcceptExtendedCriteriaDonor( patientBundle ) {
      const value = this.getSmartdataComponentValue( patientBundle, 'UHN#4009', 'valueBoolean' );
      if ( value === null ) {
        return '';
      }
      return value === 'true' ? 'Yes' : 'No';
    },
    getAcceptExtendedCriteriaDonorOATS( patientBundleOATS, organ, patientBundle ) {
      const organCode = ORGAN_LIST?.[ organ ] ?? organ;
      const organCodeNum = organCode === null || organCode === undefined ? undefined : Number( organCode );
      let journey = patientBundleOATS.journeys?.filter( ( n ) => ( n.organ_code === organCodeNum && n.state !== 'cancelled' ) );
      let episodePhase = this.getEpisodePhase( patientBundle );
      if( journey && journey[ 0 ] && journey.length === 1 ) {
        if ( journey[ 0 ].donor_acceptability?.ecd_donor === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.ecd_donor ? 'Accept' : 'Decline';
      }
      if( journey && journey[ 0 ] && journey.length > 1 ) {
        if( episodePhase > 30 ) {
          journey = journey.filter( ( n ) => ( n.stage === 'transplant' || n.stage === 'post-transplant' ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.ecd_donor === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.ecd_donor ? 'Accept' : 'Decline';
      }
        } else {
          journey = journey.filter( ( n ) => ( ( n.stage === 'registration' || n.stage === 'referral' || n.stage === 'assessment' || n.stage === 'waitlist' ) &&
          ( n.completed === false ) ) );
          if( journey && journey[ 0 ] ) {
        if ( journey[ 0 ].donor_acceptability?.ecd_donor === null ) {
          return '';
        }
        return journey[ 0 ].donor_acceptability?.ecd_donor ? 'Accept' : 'Decline';
      }
        }
      }
    },
    getDonorAcceptanceComments( patientBundle ) {
      return this.getSmartdataComponentValue( patientBundle, 'UHN#4010' );
    },
    getDonorAcceptanceCommentsOATS( patientBundleOATS, organ, patientBundle ) {
      const organCode = ORGAN_LIST?.[ organ ] ?? organ;
      const organCodeNum = organCode === null || organCode === undefined ? undefined : Number( organCode );
      let journey = patientBundleOATS.journeys?.filter( ( n ) => ( n.organ_code === organCodeNum && n.state !== 'cancelled' ) );
      let episodePhase = this.getEpisodePhase( patientBundle );
      if( journey && journey[ 0 ] && journey.length === 1 ) {
        return journey[ 0 ].donor_acceptability?.comments;
      }
      if( journey && journey[ 0 ] && journey.length > 1 ) {
        if( episodePhase > 30 ) {
          journey = journey.filter( ( n ) => ( n.stage === 'transplant' || n.stage === 'post-transplant' ) );
          if( journey && journey[ 0 ] ) {
        return journey[ 0 ].donor_acceptability?.comments;
      }
        } else {
          journey = journey.filter( ( n ) => ( ( n.stage === 'registration' || n.stage === 'referral' || n.stage === 'assessment' || n.stage === 'waitlist' ) &&
          ( n.completed === false ) ) );
          if( journey && journey[ 0 ] ) {
        return journey[ 0 ].donor_acceptability?.comments;
      }
        }
      }
    },
  },
};
</script>

<style scoped>

/* .card {
  height: 350px;
} */

.sectionHeading {
  background-color: rgb(59, 53, 53);
  color: white;
  padding-left: 5px;
}

.page-content .summary-card .card-header .summary-name {
  padding-left: 35px;
  padding-top: 4px;
  font-weight: 300;
}

.sections {
  font-size: 15px;
  height: 980px;
  width: 1400px;
  /* overflow: scroll; */
  padding-left: 10px;
  padding-top: 5px;
}

.inputDisplay {
border-style: solid;
background-color: #F2F2F2;
}

.inputDisplayPreferredDonorType {
  border-style: solid;
  width: 300px;
  background-color: #F2F2F2;
}

.fieldName {
  font-weight: 700;
  font-size: 0.875rem;
  /* padding-top: 5.5px; */
  /* padding-bottom: 2.5px; */
  /* padding-right: 15px; */
  padding-inline-start: 2%;
}

:deep(.afflo-ds-form-control) {
  width: max-content;
}

:deep(label) {
  width: max-content;
}

:deep(.preferredDonorType .afflo-ds-form-control) {
  width: 300px;
}

:deep(.comments .afflo-ds-form-control) {
  width: 300px;
}

.highlightField :deep(.afflo-ds-form-control) {
  border: 2px solid red;
}

</style>
